/* @flow */

import { call, put } from 'redux-saga/effects'
import { isObject } from 'lodash'

import { beginFetching, endFetching } from '../stores/inflightRequests'
import {
  getCookie,
  objectToQueryString,
  sessionStorageGetItem,
} from '../helpers'
import { AUTH_COOKIE, AUTH_TOKEN } from '../stores/usersStore'

export const baseOptions = {
  // to automatically send cookies for the current domain,
  // the credentials option must be provided
  credentials: 'include',
}

export async function baseFetch(url: string, options: Object = {}): Promise<*> {
  let headers = options.headers ? options.headers : {}

  /*
   * Token is added to headers in case it cannot be read from cookies, which should be the case for local development only.
   */
  if (!getCookie(AUTH_COOKIE)) {
    const token = sessionStorageGetItem(AUTH_TOKEN)
    headers['Authorization'] = `Bearer ${String(token)}`
  }

  let body
  const isFormData = options.body instanceof FormData
  if (!isFormData && isObject(options.body)) {
    body = objectToQueryString(options.body)
    headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8'
  } else {
    body = options.body
  }

  return await fetch(url, {
    ...baseOptions,
    ...options,
    headers,
    body,
  })
}

export async function loginFetch(
  url: string,
  options: Object = {}
): Promise<*> {
  const headers = {}
  let body = JSON.stringify({
    login: options.body.name,
    password: options.body.password,
    scopes: options.body.scopes,
  })

  return await fetch(url, {
    ...baseOptions,
    ...options,
    headers,
    body,
  })
}

/*
 * thin wrapper around `fetch` that handles some of
 * the peculiarities of the legacy API.
 * also dispatches actions before and after fetch
 * that get reduced (by a reducer) to a count of `inflightRequests`
 * which then can be observed to control a progress indicator or spinner.
 */
export function* sagaFetch(url: string, options: Object): Generator<*, *, *> {
  yield put(beginFetching())

  try {
    const result = yield baseFetch(url, options)
    yield put(endFetching())
    return result
  } catch (e) {
    yield put(endFetching())
    throw e
  }
}

/*
 * (to be) used by all GET requests
 */
export function* sagaGet(url: string, options: ?Object): Generator<*, *, *> {
  return yield call(sagaFetch, url, {
    method: 'GET',
    ...options,
  })
}

/*
 * (to be) used by all POST requests
 */
export function* sagaPost(
  url: string,
  data: ?Object,
  options: ?Object
): Generator<*, *, *> {
  return yield call(sagaFetch, url, {
    method: 'POST',
    body: data,
    ...options,
  })
}

export function* sagaLoginPost(url: string, data: ?Object): Generator<*, *, *> {
  return yield call(loginFetch, url, {
    method: 'POST',
    body: data,
  })
}

/*
 * (to be) used by all PUT requests
 */
export function* sagaPut(
  url: string,
  data: ?Object,
  options: ?Object
): Generator<*, *, *> {
  return yield call(sagaFetch, url, {
    method: 'PUT',
    body: data,
    ...options,
  })
}

/*
 * (to be) used by all PATCH requests
 */
export function* sagaPatch(
  url: string,
  data: ?Object,
  options: ?Object
): Generator<*, *, *> {
  return yield call(sagaFetch, url, {
    method: 'PATCH',
    body: data,
    ...options,
  })
}

/*
 * (to be) used by all DELETE requests
 */
export function* sagaDelete(url: string, data: ?Object): Generator<*, *, *> {
  return yield call(sagaFetch, url, {
    method: 'DELETE',
    body: data,
  })
}



// WEBPACK FOOTER //
// ./src/fetch/index.js